import { GridItemHTMLElement, GridStack } from 'gridstack';
import { IWidget, MergedWidget } from '../services/api/widget/types';
import { defaultEmptyWidget } from '../components/widgets/consts';
import { ref } from 'vue';

import { WidgetLayout } from '../services/api/dashboard/types';
import { debounce } from '../utils/debounce';
import { defineStore } from 'pinia';
import { useDashboardStore } from './dashboard';
import { getDefaultWidgetDimensions } from '../utils/widgetUtils';
import { __ } from './expressions';

export function createMergedWidgets(
    widgetLayout: WidgetLayout[],
    widgetData: IWidget[]
): MergedWidget[] {
    return widgetData.map((widget) => {
        const layout = widgetLayout.find((l) => l.widgetId === widget.id);
        return layout && layout.height !== null && layout.width !== null
            ? {
                  ...widget,
                  h: layout.height,
                  w: layout.width,
                  x: layout.positionX,
                  y: layout.positionY,
              }
            : { ...widget, ...getDefaultWidgetDimensions(widget) };
    });
}

export const useGrid = defineStore('grid', () => {
    const grid = ref<GridStack | null>(null);
    const widgets = ref<MergedWidget[]>([]);

    const dashboardStore = useDashboardStore();

    function mergeWidgets(widgetLayout: WidgetLayout[], widgetData: IWidget[]) {
        widgets.value = createMergedWidgets(widgetLayout, widgetData);
    }

    function loadGridItems(staticGrid?: boolean) {
        if (!grid.value) return;
        if (widgets.value.length === 0 && !staticGrid) {
            grid.value?.load([defaultEmptyWidget], true);
        } else {
            grid.value?.load(widgets.value, true);
        }
    }

    function destroyGrid() {
        if (grid.value) {
            grid.value?.destroy();
            grid.value = null;
        }
    }

    const updateDashboardLayout = debounce((dashboardId: string) => {
        const isMobile = grid.value?.getColumn() === 1;
        if (!isMobile) {
            const items = grid.value?.getGridItems();
            if (!items) return;
            const newLayout: WidgetLayout[] = items.reduce(
                (p: WidgetLayout[], c: GridItemHTMLElement) => {
                    if (!c.gridstackNode || !c.gridstackNode.id) {
                        return p;
                    }
                    const newArr = p;
                    newArr.push({
                        widgetId: c.gridstackNode.id,
                        positionX: c.gridstackNode.x ?? 0,
                        positionY: c.gridstackNode.y ?? 0,
                        width: c.gridstackNode.w ?? 0,
                        height: c.gridstackNode.h ?? 0,
                    });
                    return newArr;
                },
                [] as WidgetLayout[]
            );
            dashboardStore.updateDashboardWidgetLayout(newLayout, dashboardId);
        }
    }, 250);

    return {
        widgets,
        grid,
        mergeWidgets,
        loadGridItems,
        destroyGrid,
        updateDashboardLayout,
    };
});
